import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePage, Link } from '@inertiajs/react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu'
import { useTheme } from '@/components/theme-provider'
import { UpgradePlanModal } from '@/components/modals/UpgradePlanModal'
import { getInitials } from '@/lib/utils'
import { Sun, Moon, Monitor, ArrowLeft, Zap, Sparkles, CheckIcon, Globe } from 'lucide-react'
import { SUPPORTED_LANGUAGES } from '@/lib/languages'
import { isAdmin as checkIsAdmin } from '@/lib/auth'

interface Organization {
  id: number
  name: string
  credits_remaining: number
  current_plan: string
  admin_emails?: string[]
}

interface UserAvatarDropdownProps {
  projectId?: number
  projectName?: string
  organization?: Organization
}

export function UserAvatarDropdown({ organization }: UserAvatarDropdownProps) {
  const { t, i18n } = useTranslation('common')
  const { t: tProfile } = useTranslation('profile')
  const currentLanguage = i18n.language?.split('-')[0] || 'en'
  const { theme, setTheme } = useTheme()
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)

  const { auth } = usePage<{
    auth: {
      user: {
        name: string
        email: string
        avatar_url?: string | null
        organization_role?: string
      }
    }
  }>().props
  const initials = getInitials(auth.user?.name || '')
  const isAdmin = checkIsAdmin(auth.user)

  const backLink = '/projects'
  const backLabel = t('sidebar.backToProjects')

  // Calculate credits color class
  const getCreditsColorClass = () => {
    if (!organization) return 'text-green-600'
    const credits = Number(organization.credits_remaining)
    if (credits < 10) return 'text-red-600'
    if (credits < 50) return 'text-orange-600'
    return 'text-green-600'
  }

  const showCreditsSection = organization && organization.credits_remaining !== undefined
  const showUpgradeButton = showCreditsSection && isAdmin && Number(organization.credits_remaining) < 100

  return (
    <>
      <div className="flex items-center gap-2">
        {/* Credits & Upgrade - Left side */}
        {showCreditsSection && (
          <div className="flex shrink-0 items-center gap-1.5">
            {/* Credits badge */}
            <div className="flex items-center gap-1 rounded-md bg-muted px-2 py-1.5">
              <Zap className="h-3.5 w-3.5 shrink-0 text-yellow-500" />
              <span className={`text-xs font-semibold whitespace-nowrap ${getCreditsColorClass()}`}>
                {Number(organization.credits_remaining).toFixed(1)}
              </span>
            </div>

            {/* Upgrade button - admins only when credits < 100 */}
            {showUpgradeButton && (
              <button
                onClick={() => setIsUpgradeModalOpen(true)}
                className="flex items-center gap-1 rounded-md bg-gradient-to-r from-purple-600 to-blue-600 px-2 py-1.5 text-white text-xs font-medium whitespace-nowrap transition-none hover:from-purple-500 hover:to-blue-500"
              >
                <Sparkles className="h-3.5 w-3.5 shrink-0" />
                <span>{t('sidebar.upgradePlan')}</span>
              </button>
            )}
          </div>
        )}

        {/* User dropdown - Right side */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
              <Avatar className="h-8 w-8">
                <AvatarImage src={auth.user?.avatar_url || undefined} alt={auth.user?.name} />
                <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{auth.user?.name}</p>
                <p className="text-xs leading-none text-muted-foreground">{auth.user?.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            {/* Dashboard Link */}
            <DropdownMenuItem asChild>
              <Link href={backLink} className="cursor-pointer">
                <ArrowLeft className="mr-2 h-4 w-4" />
                <span>{backLabel}</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />

            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Sun className="mr-2 h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                <Moon className="absolute mr-2 h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                <span>{tProfile('preferences.theme')}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => setTheme('light')}>
                  <Sun className="mr-2 h-4 w-4" />
                  <span>{tProfile('preferences.themeLight')}</span>
                  {theme === 'light' && <CheckIcon className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('dark')}>
                  <Moon className="mr-2 h-4 w-4" />
                  <span>{tProfile('preferences.themeDark')}</span>
                  {theme === 'dark' && <CheckIcon className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('system')}>
                  <Monitor className="mr-2 h-4 w-4" />
                  <span>{tProfile('preferences.themeSystem')}</span>
                  {theme === 'system' && <CheckIcon className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Globe className="mr-2 h-4 w-4" />
                <span>{tProfile('preferences.language')}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {SUPPORTED_LANGUAGES.map(({ code, nativeName }) => (
                  <DropdownMenuItem key={code} onClick={() => i18n.changeLanguage(code)}>
                    <span>{nativeName}</span>
                    {currentLanguage === code && <CheckIcon className="ml-auto h-4 w-4" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <UpgradePlanModal
        open={isUpgradeModalOpen}
        onOpenChange={setIsUpgradeModalOpen}
      />
    </>
  )
}
